import { motion } from "framer-motion";
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Award, TrendingUp } from "lucide-react";
import { topAssists, topScorers } from "../../data/sep26Data";
import { Empty26 } from "./Empty26";
import { EASE_OUT } from "./hooks";

const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const { dataKey, value } = payload[0];
  return (
    <div className="border p-3" style={{ background: "var(--ink)", borderColor: "var(--gold)" }}>
      <div className="label">{label}</div>
      <div className="mt-1" style={{ color: "var(--gold)" }}>{value} {dataKey === "goals" ? "goles" : "asistencias"}</div>
    </div>
  );
};

const Chart = ({ title, Icon, data, dataKey, color, testId }) => (
  <div className="border p-6 sm:p-8" style={{ borderColor: "var(--line)", background: "rgba(18,26,69,.5)" }} data-testid={testId}>
    <div className="mb-6 flex items-center gap-3">
      <span className="grid h-10 w-10 place-items-center" style={{ background: color, color: "var(--ink)" }}>
        <Icon size={18} aria-hidden="true" />
      </span>
      <h3 className="display text-3xl">{title}</h3>
    </div>
    <div className="h-72 w-full min-w-0">
      <ResponsiveContainer width="100%" height="100%" minHeight={280} minWidth={0}>
        <BarChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="2 4" stroke="rgba(243,238,226,.1)" />
          <XAxis dataKey="name" tick={{ fill: "#F3EEE2", fontSize: 12 }} axisLine={{ stroke: "rgba(243,238,226,.25)" }} tickLine={false} />
          <YAxis allowDecimals={false} tick={{ fill: "rgba(243,238,226,.55)", fontSize: 11 }} axisLine={false} tickLine={false} />
          <Tooltip content={<Tip />} cursor={{ fill: "rgba(243,238,226,.05)" }} />
          <Bar dataKey={dataKey}>
            {data.map((d, i) => (
              <Cell key={d.name} fill={color} fillOpacity={1 - i * 0.12} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>
);

export const Estadisticas26 = () => {
  const empty = topScorers.length === 0 && topAssists.length === 0;

  return (
    <section id="estadisticas" data-testid="s26-estadisticas" className="section-pad" style={{ background: "var(--navy-deep)" }}>
      <div className="wrap">
        <motion.div
          className="mb-10"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE_OUT }}
        >
          <h2 className="display text-[clamp(44px,8vw,112px)]">
            Los números <span style={{ color: "var(--gold)" }}>hablan</span>
          </h2>
          <p className="serif mt-4 max-w-[40ch] text-xl" style={{ color: "var(--muted)" }}>
            Goleadores y asistencias de la temporada.
          </p>
        </motion.div>

        {empty ? (
          <Empty26
            testId="s26-estadisticas-vacio"
            title="Aún no hay goles que contar"
            text="Los goleadores y los pasadores de la temporada aparecen aquí desde la primera jornada."
          />
        ) : (
          <div className="grid gap-6 lg:grid-cols-2">
            <Chart
              title="Goleadores"
              Icon={Award}
              data={topScorers.map((p) => ({ name: p.nickname ?? p.name, goals: p.goals }))}
              dataKey="goals"
              color="#E0B04A"
              testId="s26-goleadores"
            />
            <Chart
              title="Asistencias"
              Icon={TrendingUp}
              data={topAssists.map((p) => ({ name: p.nickname ?? p.name, assists: p.assists }))}
              dataKey="assists"
              color="#F3EEE2"
              testId="s26-asistencias"
            />
          </div>
        )}
      </div>
    </section>
  );
};
